import { useState } from 'react';
import { AlertTriangle, Info, RotateCcw, Search, User as UserIcon, Users } from '../../../icons';
import type { MenuAccessState } from '../../../api/menu-access';
import type { Verdict } from '../../../lib/menu-judge';
import { TriState } from './TriState';
import { Pills, ResultCell, SectionRow } from './DeptTab';
import {
  groupBySection, judgeFor, orderedMenus, peopleOf, personViewer, ruleAt, rulesOf, unitLabel,
  type OrgModel, type Person, type Unit,
} from './model';

/**
 * 「개인별 권한」 탭 (확정 시안 v4 · 사용자 결정 Q2).
 *
 * 개인 설정은 **부서 기준 결과 위에 메뉴 하나씩 덮어쓰는 층**이다. 개인 설정이 없는 메뉴는 늘 부서 결과를 따르고,
 * 되돌리기 = 부서 기준으로 복귀. 표 = 「부서 설정대로라면 → 개인 설정 → 최종」.
 *
 * 태그: 「개인 설정이 우선」(결과가 달라짐) · 「부서와 같음」(없어도 됨) · 「적용 안 됨 · 회사에서 끔 / 계약 모듈」.
 */
interface Props {
  state: MenuAccessState;
  org: OrgModel;
  unit: Unit;
  personId: string | null;
  busy: boolean;
  onSelectPerson: (id: string) => void;
  onCell: (personId: string, menuKey: string, next: Verdict) => void;
  onClearPerson: (person: Person) => void;
}

type Filter = 'all' | 'ruled' | 'hidden';

export function PersonTab({ state, org, unit, personId, busy, onSelectPerson, onCell, onClearPerson }: Props) {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const people = peopleOf(org, unit);
  const shownPeople = q.trim() ? people.filter((p) => p.name.includes(q.trim())) : people;
  const person = personId ? people.find((p) => p.id === personId) ?? null : null;
  const teamName = (id: string | null) => (id ? org.teams.get(id)?.name ?? '팀' : null);

  return (
    <div className="mpx-split">
      <div className="mpx-people">
        <div className="mpx-find">
          <Search size={14} aria-hidden />
          <input id="mpx-people-q" type="search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={`${people.length}명 중 찾기`} aria-label="사람 찾기" />
        </div>
        <div className="mpx-people__list">
          {people.length === 0 && <p className="mpx-dim mpx-pad">이 조직에 사람이 없습니다.</p>}
          {shownPeople.map((p) => {
            const ov = rulesOf(state, 'user', p.id).length;
            const extra = p.teamIds.length > 1 ? ` 외 ${p.teamIds.length - 1}` : '';
            return (
              <button key={p.id} type="button" className={`mpx-person${p.id === personId ? ' is-on' : ''}`}
                aria-current={p.id === personId || undefined} onClick={() => onSelectPerson(p.id)}>
                <span className="mpx-avatar" aria-hidden>{p.name.slice(0, 1)}</span>
                <span className="mpx-person__who">
                  <b>{p.name}</b>
                  <span>{[p.position, (teamName(p.judgeTeamId) ?? '팀 없음') + extra].filter(Boolean).join(' · ')}</span>
                </span>
                {ov > 0 && <span className="mpx-count" title={`개인 설정 ${ov}개`}>{ov}</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mpx-person-pane">
        {people.length === 0 ? (
          <div className="mpx-empty">
            <span className="mpx-empty__ico" aria-hidden><Users size={20} /></span>
            <b>{unitLabel(org, unit)}에 사람이 없습니다</b>
            <p>조직도에서 사람을 이 팀에 넣으면 여기서 한 사람씩 권한을 정할 수 있습니다.</p>
          </div>
        ) : !person ? (
          <div className="mpx-empty">
            <span className="mpx-empty__ico" aria-hidden><UserIcon size={20} /></span>
            <b>왼쪽에서 사람을 고르세요</b>
            <p>이름 옆 숫자는 따로 정한 메뉴 수입니다. 개인 설정이 없는 메뉴는 부서 설정을 따릅니다.</p>
          </div>
        ) : (
          (() => {
            const viewer = personViewer(org, person);
            const deptViewer = { ...viewer, userId: null };
            const own = rulesOf(state, 'user', person.id);
            const menus = orderedMenus(state);
            const rows = menus.map((m) => ({
              m,
              base: judgeFor(state, m, deptViewer),
              j: judgeFor(state, m, viewer),
              own: ruleAt(state, 'user', person.id, m.menuKey),
            }));
            const counts = {
              all: rows.length,
              ruled: rows.filter((r) => r.own !== 'none').length,
              hidden: rows.filter((r) => !r.j.visible).length,
            };
            const shown = new Set(rows
              .filter((r) => (filter === 'ruled' ? r.own !== 'none' : filter === 'hidden' ? !r.j.visible : true))
              .map((r) => r.m.menuKey));
            const byKey = new Map(rows.map((r) => [r.m.menuKey, r]));
            const groups = groupBySection(menus).map(([s, list]) => [s, list.filter((m) => shown.has(m.menuKey))] as const)
              .filter(([, list]) => list.length > 0);
            const judgeTeam = person.judgeTeamId ? org.teams.get(person.judgeTeamId) : undefined;
            const divName = judgeTeam?.divisionId ? org.divisions.find((d) => d.id === judgeTeam.divisionId)?.name : null;
            return (
              <>
                <div className="mpx-person-head">
                  <span className="mpx-avatar mpx-avatar--lg" aria-hidden>{person.name.slice(0, 1)}</span>
                  <span className="mpx-person__who">
                    <b>{person.name}</b>
                    <span>{[person.position, judgeTeam ? [divName, judgeTeam.name].filter(Boolean).join(' › ') : '팀없는직원'].filter(Boolean).join(' · ')}</span>
                  </span>
                  <span className="mpx-sp" />
                  <button type="button" className="mpx-btn mpx-btn--quiet" disabled={busy || own.length === 0} onClick={() => onClearPerson(person)}>
                    <RotateCcw size={13} aria-hidden /> 개인 설정 {own.length}개 되돌리기 — 부서 기준으로
                  </button>
                </div>
                {person.teamIds.length > 1 && (
                  <p className="mpx-note mpx-note--warn">
                    <AlertTriangle size={14} aria-hidden />
                    <span>{person.name} 님은 <b>{person.teamIds.map((t) => teamName(t)).join(' · ')}</b> {person.teamIds.length}개 팀에 있습니다.
                      부서 기준은 먼저 들어간 <b>{teamName(person.judgeTeamId) ?? '팀'}</b>입니다.</span>
                  </p>
                )}
                {!person.judgeTeamId && (
                  <p className="mpx-note mpx-note--info">
                    <Info size={14} aria-hidden />
                    <span>{person.name} 님은 팀이 없어 <b>부서 기준 = 회사 설정</b>입니다. 이 사람만 다르게 할 메뉴를 여기서 정합니다.</span>
                  </p>
                )}
                <div className="mpx-toolbar"><Pills counts={counts} value={filter} onChange={setFilter} /></div>
                <div className="mpx-scroll">
                  <table className="mpx-table mpx-table--person">
                    <thead>
                      <tr><th scope="col">메뉴</th><th scope="col">부서 설정대로라면</th><th scope="col">개인 설정</th><th scope="col">최종</th></tr>
                    </thead>
                    <tbody>
                      {groups.length === 0 && <tr><td colSpan={4} className="mpx-dim">이 조건에 맞는 메뉴가 없습니다.</td></tr>}
                      {groups.map(([section, list]) => [
                        <SectionRow key={`s-${section}`} section={section} cols={4} />,
                        ...list.map((m) => {
                          const r = byKey.get(m.menuKey)!;
                          const tag = r.j.userState === 'applied'
                            ? <span className="mpx-tag mpx-tag--info">개인 설정이 우선</span>
                            : r.j.userState === 'same'
                              ? <span className="mpx-tag" title="부서 기준과 결과가 같습니다 — 되돌려도 달라지지 않습니다">부서와 같음</span>
                              : r.j.userState === 'blocked'
                                ? <span className="mpx-tag mpx-tag--warn">적용 안 됨 · {r.j.blockedBy === 'module' ? '계약 모듈' : '회사에서 끔'}</span>
                                : null;
                          return (
                            <tr key={m.menuKey} className={r.own !== 'none' ? 'is-ruled' : undefined}>
                              <th scope="row"><span className="mpx-mname__t">{m.label}</span></th>
                              <td><ResultCell org={org} j={r.base} /></td>
                              <td>
                                <span className="mpx-stack">
                                  <TriState
                                    value={r.own}
                                    noneLabel="부서 기준"
                                    label={`${m.label} — ${person.name}`}
                                    allowLockedReason={r.j.blockedBy === 'company'
                                      ? '회사에서 끈 메뉴는 허용으로 열 수 없습니다'
                                      : r.j.blockedBy === 'module' ? '계약 모듈이 꺼진 메뉴는 허용으로 열 수 없습니다' : null}
                                    disabled={busy}
                                    onChange={(next) => onCell(person.id, m.menuKey, next)}
                                  />
                                  {tag}
                                </span>
                              </td>
                              <td>
                                <span className={`mpx-badge ${r.j.visible ? 'mpx-badge--ok' : 'mpx-badge--bad'}`}>{r.j.visible ? '보임' : '감춤'}</span>
                              </td>
                            </tr>
                          );
                        }),
                      ])}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()
        )}
      </div>
    </div>
  );
}
